import Tracker, { AmplitudeTracker, UserRecorderTracker, GTMTracker } from '@rentspree/tracker'
import Amplitude from 'amplitude-js'
import get from 'lodash/get'
import isEmpty from 'lodash/isEmpty'
import omitBy from 'lodash/omitBy'
import set from 'lodash/set'

import env from 'env'

type CreateTrackerParams = {
  amplitude?: boolean
  userRecorder?: boolean
  gtm?: boolean
}

export const generateAmplitudeProfileItem = <T>(value: T, setOnce = false) => ({
  value,
  setOnce,
})

export const createAmplitudeTracker = () => {
  const amplitudeTracker = new AmplitudeTracker({
    apiKey: env.AMPLITUDE_API_KEY,
    amplitudeSDK: Amplitude,
    amplitudeConfig: {
      domain: env.AMPLITUDE_COOKIE_DOMAIN,
      saveParamsReferrerOncePerSession: false, // Always capture a new UTM value
      includeUtm: true,
    },
    mapUserIdentity: (profile) => get(profile, '_id') as string,
    mapUserProfile: (profile) => {
      const initProfile = {
        email: generateAmplitudeProfileItem(profile.email),
        phone: generateAmplitudeProfileItem(profile.phone),
        createdAt: generateAmplitudeProfileItem(profile.createdAt, true),
        userType: generateAmplitudeProfileItem(profile.userType),
        registeredUserType: generateAmplitudeProfileItem(profile.registeredUserType),
      }
      if (!isEmpty(profile.firstName)) {
        set(initProfile, 'firstName', generateAmplitudeProfileItem(profile.firstName))
      }
      if (!isEmpty(profile.lastName)) {
        set(initProfile, 'lastName', generateAmplitudeProfileItem(profile.lastName))
      }
      if (!isEmpty(profile.experiments)) {
        profile?.experiments?.forEach(({ name, value }) => {
          initProfile[name] = { value }
        })
      }
      return omitBy(initProfile, (item) => {
        const { value } = item
        return isEmpty(value)
      })
    },
  })
  return amplitudeTracker
}

export const createGtmTracker = () =>
  new GTMTracker({
    trackingId: env.GOOGLE_TAG_MANAGER_CONTAINER_ID,
    mapUserProfile: (profile) => ({
      id: get(profile, '_id') as string,
      email: get(profile, 'email') as string,
    }),
    mapUserIdentity: (profile) => get(profile, '_id') as string,
  })

export const createTracker = ({ amplitude, userRecorder, gtm }: CreateTrackerParams) => {
  if (typeof window === 'undefined') {
    return undefined
  }
  const gtmTracker = createGtmTracker()
  const userRecorderTracker = new UserRecorderTracker()
  const amplitudeTracker = createAmplitudeTracker()
  const tracker = new Tracker()

  if (amplitude) {
    tracker.registerTracker(amplitudeTracker)
  }

  if (userRecorder) {
    tracker.registerTracker(userRecorderTracker)
  }

  if (gtm) {
    tracker.registerTracker(gtmTracker)
  }

  return tracker
}

export default createTracker({ amplitude: true, userRecorder: true, gtm: true })
export const createCustomTracker = createTracker
